<script setup>
import { cloneDeep, isEmpty, isNull } from 'lodash'
import { computed, ref } from 'vue'
import { SlickItem, SlickList, HandleDirective as vHandle } from 'vue-slicksort'

import OkrFiltersApiHandler from '@/api/okr-filters'
import { ACTIONS_KEYS } from '@/utils/actions-keys'
import { handleOrder } from '@/utils/drag-n-drop'
import { handleError } from '@/utils/error-handling'
import { numberOrNullProp } from '@/utils/prop-validators'

import AppDroplist from '@/components/AppDroplist'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppIcon from '@/components/ui/AppIcon/AppIcon'
import DropdownMenu from '@/components/ui/DropdownMenu/DropdownMenu'
import CheckinsFeedFilterButton from '@/views/check-ins/CheckinsFeedFilterButton'

defineOptions({
  name: 'CheckinFiltersDropdown'
})

const props = defineProps({
  filters: {
    type: Array,
    default: () => []
  },

  loading: {
    type: Boolean
  },

  activeFilterId: {
    required: true,
    validator: v => numberOrNullProp(v)
  },

  isTriggerActive: {
    type: Boolean
  },

  hideContent: {
    type: Boolean
  }
})

const model = defineModel({
  type: Boolean,
  required: true
})

const emit = defineEmits({
  'update-filters': filters => Array.isArray(filters),
  'reload-filters': null,
  'apply-filter': null,
  'edit-filter': null,
  'remove-filter': null
})

const updateFilters = (filters = []) => {
  emit('update-filters', cloneDeep(filters))
}

const isUpdateOrderLoading = ref(false)

const isDragDisabled = computed(() => isUpdateOrderLoading.value || props.loading)

const onSortEnd = async ({ oldIndex, newIndex }) => {
  if (isDragDisabled.value) {
    return
  }

  if (isNull(newIndex) || oldIndex === newIndex) {
    return
  }

  const filterForUpdate = props.filters[oldIndex]

  if (!filterForUpdate) {
    return
  }

  const copy = cloneDeep(props.filters)

  copy.splice(newIndex, 0, copy.splice(oldIndex, 1)[0])

  updateFilters(copy)

  const { orderValue } = handleOrder({
    oldIndex,
    newIndex,
    data: copy
  })

  const payload = {
    ...cloneDeep(filterForUpdate),
    orderValue
  }

  const api = new OkrFiltersApiHandler()

  isUpdateOrderLoading.value = true
  try {
    await api.updateCustomFilterOrder({
      filterViewId: filterForUpdate.id,
      ...payload
    })

    emit('reload-filters')
  } catch (error) {
    handleError({ error })
  } finally {
    isUpdateOrderLoading.value = false
  }
}

const localFilters = computed({
  get: () => props.filters,
  set: v => updateFilters(v)
})

const MENU_ITEM_ACTIONS = [
  {
    name: ACTIONS_KEYS.EDIT,
    title: 'action.edit',
    icon: 'edit-next'
  },
  {
    name: ACTIONS_KEYS.REMOVE,
    title: 'action.delete',
    icon: 'delete-next',
    color: 'var(--grade-low-color-next)'
  }
]

const onMenuActionsClick = (name, filter) => {
  if (name === ACTIONS_KEYS.EDIT) {
    emit('edit-filter', filter)
  }
  if (name === ACTIONS_KEYS.REMOVE) {
    emit('remove-filter', filter)
  }
}
</script>

<template>
  <div class="cfd-DropdownWrapper">
    <AppDroplist
      v-model="model"
      :offset="[0, 8]"
      append-to=".cfd-DropdownWrapper"
      class="cfd-Dropdown"
      dropdown-width="240px"
      position="bottom-start"
    >
      <SlickList
        v-if="!isEmpty(localFilters)"
        v-show="!hideContent"
        :distance="10"
        :dragged-settling-duration="1"
        :list="localFilters"
        :lock-offset="['5%', '5%']"
        :transition-duration="200"
        append-to=".cfd-Dropdown"
        class="cfd-FiltersList"
        helper-class="cdf-FiltersList_Item-grabbing"
        lock-axis="y"
        lock-to-container-edges
        use-drag-handle
        @sort-end="onSortEnd({ ...$event })"
      >
        <SlickItem
          v-for="(filter, index) in localFilters"
          :key="filter.id"
          :class="{ 'cdf-FiltersList_Item-active': filter.id === activeFilterId }"
          :index="index"
          class="cdf-FiltersList_Item"
          @click="emit('apply-filter', filter)"
        >
          <AppIcon
            v-handle
            :class="{
              'cfd-Drag-disabled': isDragDisabled
            }"
            class="cfd-Drag"
            height="24"
            icon-name="drag-next"
            width="24"
            @click.stop
          />

          <span class="oboard-truncated-text"> {{ filter.name }} </span>

          <div class="cfd-Menu" @click.stop>
            <DropdownMenu
              :items="MENU_ITEM_ACTIONS"
              :offset="[0, 0]"
              append-to="parent"
              position="bottom-end"
              type="default-next"
              @item-click="onMenuActionsClick($event, filter)"
            >
              <template #activator="{ isOpened }">
                <AppButton
                  :class="{ 'cfd-ActionsButton-active': isOpened }"
                  class="cfd-ActionsButton"
                  icon="more-next"
                  size="sm"
                  type="tertiary-next"
                />
              </template>
            </DropdownMenu>
          </div>
        </SlickItem>
      </SlickList>

      <template #button="{ expanded }">
        <CheckinsFeedFilterButton :active="expanded || isTriggerActive" remove-padding>
          …
        </CheckinsFeedFilterButton>
      </template>
    </AppDroplist>
  </div>
</template>

<style lang="scss" scoped>
@import '~@/assets/styles/dnd';
@import '~@/assets/styles/mixins';

.cfd-DropdownWrapper {
  user-select: none;
}

.cfd-Drag {
  cursor: grab;
  flex-shrink: 0;

  &-disabled {
    opacity: 0.3;
    pointer-events: none;
  }

  .cdf-FiltersList_Item-grabbing & {
    cursor: grabbing;
  }
}

.cfd-FiltersList {
  max-height: 396px;
  overflow-y: auto;
  border-radius: inherit;
  @include styled-native-scrollbar();
  overflow-x: hidden;
}

.cdf-FiltersList_Item {
  display: flex;
  align-items: center;
  height: 44px;
  padding-inline: 8px;
  gap: 4px;
  cursor: pointer;
  font-size: $fs-14;
  line-height: 20px;

  &-active {
    background-color: rgba($primary-color-next, 0.15);
  }

  &:not(&-active):hover {
    background-color: $grey-3-next;
  }
}

.cfd-Menu {
  margin-left: auto;
  flex-shrink: 0;
}

.cfd-ActionsButton {
  background-color: transparent;
  color: $grey-1-next;
  &-active {
    background-color: $grey-2-next;
    color: $dark-2;
  }
}

// eslint-disable-next-line vue-scoped-css/no-unused-selector
.cdf-FiltersList_Item-grabbing {
  @extend %grabbing-item-common-styles;
  @extend %grabbing-item-shadow;
  background-color: $white !important;
  @include grabbingItemBackdrop(calc(100% + 120px), -120px);
}
</style>
